import { Card } from '../types';

/**
 * Get the image path for a card
 * Uses cards1 folder with naming convention: {rank}{suit}.png
 * Where: Jack=11, Queen=12, King=13, Ace=14
 * Suits: s=spades, h=hearts, d=diamonds, c=clubs
 */
export function getCardImagePath(card: Card): string {
  // Handle null/undefined card
  if (!card) {
    try {
      return require('../../assets/images/tableImages/backCard1.png');
    } catch {
      return '';
    }
  }

  // Handle face-down cards
  if (!card.isFaceUp) {
    try {
      return require('../../assets/images/tableImages/backCard1.png');
    } catch {
      return '';
    }
  }

  // Handle missing suit or rank
  if (!card.suit || card.rank === undefined || card.rank === null) {
    try {
      return require('../../assets/images/tableImages/backCard1.png');
    } catch {
      return '';
    }
  }

  const suit = card.suit.toLowerCase();
  const rank = card.rank;

  // Map suit to single letter
  const suitMap: { [key: string]: string } = {
    spades: 's',
    hearts: 'h',
    diamonds: 'd',
    clubs: 'c',
  };

  // Normalize suit to single letter
  const suitLetter = suit.length === 1 ? suit : suitMap[suit] || suit;

  // Use simple naming convention: {rank}{suit}.png
  // e.g., "14s.png" for Ace of Spades, "11h.png" for Jack of Hearts
  try {
    return require(`../../assets/images/cards1/${suitLetter}${rank}.png`);
  } catch {
    // Fallback to back card if image not found
    try {
      return require('../../assets/images/tableImages/backCard1.png');
    } catch {
      return '';
    }
  }
}

/** Safe version that never throws - use during showdown/celebration to avoid crashing the app */
export function getSafeCardImagePath(card: Card | null | undefined): string {
  try {
    return getCardImagePath(card ?? { rank: 0, suit: '?', isFaceUp: false });
  } catch {
    try {
      return require('../../assets/images/tableImages/backCard1.png');
    } catch {
      return '';
    }
  }
}

/**
 * Parse card from string format
 * Supports both "14-s" (with hyphen) and "13d" (without hyphen) formats
 * e.g., "14-s" or "13d" for Ace of Spades or King of Diamonds
 */
export function parseCard(cardStr: string): Card {
  if (!cardStr || cardStr.length < 2) {
    console.warn('parseCard: Invalid card string:', cardStr);
    return { rank: 0, suit: '?', isFaceUp: false };
  }

  // Trim whitespace
  const trimmed = cardStr.trim();

  // Check if it's in "rank-suit" format (with hyphen)
  if (trimmed.includes('-')) {
    const [rankStr, suit] = trimmed.split('-');
    const rank = parseInt(rankStr, 10);
    if (isNaN(rank) || rank < 1 || rank > 14) {
      console.warn('parseCard: Invalid rank:', rankStr, 'from card:', trimmed);
      return { rank: 0, suit: '?', isFaceUp: false };
    }
    return {
      rank: rank,
      suit: (suit || '?').toLowerCase(),
      isFaceUp: true,
    };
  }

  // Otherwise, it's in "ranksuit" format (e.g., "13d", "2h")
  // Extract rank (can be 1-2 digits) and suit (single letter)
  const match = cardStr.match(/^(\d+)([shdc])$/);
  if (match) {
    return {
      rank: parseInt(match[1], 10),
      suit: match[2],
      isFaceUp: true,
    };
  }

  // Fallback for invalid format
  return { rank: 0, suit: '?', isFaceUp: false };
}

/**
 * Convert card to string format
 */
export function cardToString(card: Card): string {
  return `${card.rank}-${card.suit}`;
}

/**
 * Convert card to format used in playerBestHand (e.g., "11s" for rank 11, suit s)
 */
export function cardToBestHandFormat(card: Card): string {
  if (!card || !card.suit || card.rank === undefined) {
    return '';
  }
  const suit = card.suit.toLowerCase();
  const suitMap: { [key: string]: string } = {
    spades: 's',
    hearts: 'h',
    diamonds: 'd',
    clubs: 'c',
  };
  const suitLetter = suit.length === 1 ? suit : suitMap[suit] || suit;
  return `${card.rank}${suitLetter}`;
}

